import jsCookie from 'js-cookie'
import {usePathname} from 'next/navigation'
import {FC, useEffect, useRef, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {getCookiesContent} from '@/content/cookies'
import {Trans, useTranslation} from '@/i18n'
import {
  HEADER_HEIGHT_MOBILE,
  HEADER_PADDING_MOBILE,
  MISSIVE_BUTTON_OFFSET,
  MISSIVE_BUTTON_SIZE,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {otherCookiesAllowedCookieName} from '@/utils/constants/http'
import {useLanguage} from '@/utils/i18n/language'
import {parseToLocaleAndCleanedPath} from '@/utils/i18n/restrictedPaths'
import {isMissiveChatShown} from '@/utils/missive/helpers'
import {getVisitorCountry} from '@/utils/preferences/country'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../common/content/Text'
import {TransLink} from '../common/TransLink'
import {CookiesModal} from './CookiesModal'
import {handleConsent, requiresPriorConsent} from './utils'

type ConsentState = 'unset' | 'granted' | 'denied'

export const CookiesBanner: FC = () => {
  const {t} = useTranslation()
  const language = useLanguage()
  const pathname = usePathname()
  const {ariaLabel, description, infoLinkHref, buttonConsentTitle, buttonRejectTitle} =
    getCookiesContent(language).banner
  const missiveChatVisible = isMissiveChatShown(pathname ?? '/')

  // Consent is only readable in the browser, so nothing is rendered during SSR and
  // the banner decides once mounted. That keeps the pages rendering it free of
  // `cookies()`, and therefore statically renderable.
  // `null` until the cookie is read on the client; `'unset'` means the user has not
  // decided yet, so a stored refusal (`'denied'`) keeps the banner hidden.
  const [consent, setConsent] = useState<ConsentState | null>(null)
  const [cookiesModalOpen, setCookiesModalOpen] = useState(false)
  const bannerRef = useRef<HTMLElement>(null)
  const isCookiesPage = parseToLocaleAndCleanedPath(pathname ?? '/').cleanedPath.startsWith(
    '/cookies',
  )
  const isBannerVisible = consent === 'unset' && !isCookiesPage

  const saveConsent = (allowed: boolean) => {
    handleConsent(allowed)
    setConsent(allowed ? 'granted' : 'denied')
  }

  useEffect(() => {
    const storedConsent = jsCookie.get(otherCookiesAllowedCookieName)
    if (storedConsent !== undefined) {
      setConsent(storedConsent === 'true' ? 'granted' : 'denied')
      return
    }
    // The country from its own source rather than through `usePreferences()`, which keeps the
    // banner free of request-scoped state, so it can live in the shared shell. Decided only once
    // the country is known; an unknown country asks for consent.
    const decide = async () => {
      const country = await getVisitorCountry()
      if (!requiresPriorConsent(country)) {
        // No banner here, so grant on arrival - otherwise the layout's consent listeners
        // would never fire at all and trackers would stay off in opt-out countries.
        saveConsent(true)
        return
      }
      setConsent('unset')
    }
    void decide()
  }, [])

  // The banner is fixed to the bottom of the viewport, so keep the page from scrolling a focused
  // element underneath it. On tablet it sits above the mobile header, so its offset counts too.
  useEffect(() => {
    const banner = bannerRef.current
    if (!isBannerVisible || !banner) {
      return
    }
    const root = document.documentElement
    const updateScrollPadding = () => {
      const bottomOffset = parseFloat(getComputedStyle(banner).bottom) || 0
      root.style.scrollPaddingBottom = `${banner.offsetHeight + bottomOffset}px`
    }
    updateScrollPadding()
    const resizeObserver = new ResizeObserver(updateScrollPadding)
    resizeObserver.observe(banner)
    window.addEventListener('resize', updateScrollPadding)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateScrollPadding)
      root.style.scrollPaddingBottom = ''
    }
  }, [isBannerVisible])

  if (consent === null || isCookiesPage) {
    return null
  }

  return (
    <>
      {isBannerVisible && (
        <section
          ref={bannerRef}
          className="wrapper"
          aria-label={t(ariaLabel)}
          data-nosnippet="data-nosnippet"
        >
          <div className="description">
            <Text
              variant={TextVariant.text14}
              mobileVariant={TextVariant.caption}
              element="span"
              color={theme.palette.text.white}
            >
              <Trans
                t={t}
                i18nKey={description}
                components={{
                  1: (
                    <button
                      type="button"
                      className="manage-button"
                      onClick={() => setCookiesModalOpen(true)}
                    >
                      text
                    </button>
                  ),
                  2: (
                    <TransLink
                      href={infoLinkHref}
                      color={theme.palette.text.white}
                      fontWeight={TextFontWeight.bold}
                    >
                      text
                    </TransLink>
                  ),
                }}
              />
            </Text>
          </div>
          <div className="buttons">
            <Button
              variant={ButtonVariant.tertiary}
              size={ButtonSize.small}
              textVariant={TextVariant.subtitle11}
              type="button"
              onClick={() => saveConsent(false)}
            >
              {t(buttonRejectTitle)}
            </Button>
            <Button
              variant={ButtonVariant.secondary}
              size={ButtonSize.small}
              textVariant={TextVariant.subtitle11}
              type="button"
              onClick={() => saveConsent(true)}
            >
              {t(buttonConsentTitle)}
            </Button>
          </div>
        </section>
      )}
      <CookiesModal
        isVisible={cookiesModalOpen}
        closeModal={() => setCookiesModalOpen(false)}
        otherAllowed={consent === 'granted'}
        saveConsent={saveConsent}
      />

      <style jsx>{`
        .wrapper {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: ${theme.palette.tertiary.main};
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: ${theme.spacing[4]};
          border-top: 1px solid ${theme.palette.gray[600]};
        }
        .description {
          flex: 1;
        }
        .manage-button {
          padding: 0;
          border: 0;
          background: none;
          font: inherit;
          font-weight: ${theme.fontWeight.medium};
          color: ${theme.palette.text.primary};
          text-decoration: underline;
          cursor: pointer;
        }
        .buttons {
          display: flex;
          align-items: center;
          gap: ${theme.spacing[2]};
          margin-left: ${theme.spacing[5]};
          margin-right: ${missiveChatVisible
            ? `calc(${MISSIVE_BUTTON_SIZE} + ${MISSIVE_BUTTON_OFFSET})`
            : 0};
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            display: block;
            text-align: center;
            bottom: calc(${HEADER_HEIGHT_MOBILE} + ${theme.spacing[1.5]});
            padding: ${theme.spacing[2.5]};
            margin: 0 ${HEADER_PADDING_MOBILE};
            border-radius: 20px;
            border: 1px solid ${theme.palette.gray[600]};
          }
          .buttons {
            justify-content: center;
            margin-left: 0;
            margin-right: 0;
            margin-top: ${theme.spacing[2.5]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .buttons {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </>
  )
}
